import { SelectOptionType } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'reactstrap';

import IdentificationTypeLoader, {
  useIdentificationTypeLoader,
} from '../../../../components/loaders/IdentificationTypeLoader';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import SectionTitle from '../../../../components/text/SectionTitle';
import { useParameters } from '../../../../hooks/useParameters';
import {
  getSchoolsForSelect,
  getStudentList,
  getStudyPlansForSelect,
} from './api';
import StudentListFilters from './components/StudentListFilters';
import {
  StudentListConsumer,
  StudentListProvider,
} from './components/StudentListLoader';
import StudentListTable from './components/StudentListTable';
import { StudentListQuery } from './types';

const DEFAULT_QUERY: StudentListQuery = { page: 0, items_per_page: 10 };

function StudentListContent() {
  const { t } = useTranslation();
  const { data: identificationTypeData } = useIdentificationTypeLoader();

  const [schools, setSchools] = useState<SelectOptionType[]>([]);
  const [studyPlans, setStudyPlans] = useState<SelectOptionType[]>([]);

  const request = useCallback(async (query: StudentListQuery) => {
    return getStudentList(query);
  }, []);

  useEffect(() => {
    getSchoolsForSelect().then((result) => {
      if (result.data) {
        setSchools(
          result.data.data.map((s) => ({ value: s.id, label: s.name })),
        );
      }
    });
    getStudyPlansForSelect().then((result) => {
      if (result.data) {
        setStudyPlans(
          result.data.data.map((sp) => ({ value: sp.id, label: sp.name })),
        );
      }
    });
  }, []);

  const identificationTypes = useMemo(() => {
    return (identificationTypeData?.identificationTypes ?? []).map((it) => ({
      value: it.id,
      label: it.name,
    }));
  }, [identificationTypeData]);

  return (
    <Card className="mt-5 mx-3 p-4">
      <SectionTitle text={t('financialPortal.currentAccount.list.title')} />
      <p>{t('financialPortal.currentAccount.list.subtitle')}</p>

      <StudentListProvider
        request={request}
        defaultQuery={DEFAULT_QUERY}
        fetchImmediately
      >
        <StudentListFilters
          identificationTypes={identificationTypes}
          schools={schools}
          studyPlans={studyPlans}
        />
        <StudentListConsumer>
          {({ loading, data, error, refresh }) => {
            if (loading && !data) {
              return <Loading insideCard />;
            }
            if (error) {
              return (
                <DisplayError
                  insideCard
                  textBody={error.code}
                  retryAction={refresh}
                />
              );
            }
            return <StudentListTable />;
          }}
        </StudentListConsumer>
      </StudentListProvider>
    </Card>
  );
}

export default function StudentListView() {
  const { defaultCountry } = useParameters();

  return (
    <IdentificationTypeLoader countryId={defaultCountry ?? null}>
      <StudentListContent />
    </IdentificationTypeLoader>
  );
}
